import React, { useMemo } from 'react';
import axios from 'axios';
import { IoClose } from "react-icons/io5";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

const FilterFeatureDataSimpleTimeseriesModal = ({ globalRefs, globalState }) => {
    const mapMetaData = globalState.mapMetaData;
    const setShowFilterFeatureDataSimpleTimeseriesModal = globalState.setShowFilterFeatureDataSimpleTimeseriesModal;
    const setMapMetaData = globalState.setMapMetaData;
    const currentMapIdRef = globalRefs.currentMapIdRef;
    const workingLocally = globalState.workingLocally;
    const devDeployment = globalState.devDeployment;
    const availableNumericMetrics =
        globalState.availableNumericMetrics || [];
    const availableStringMetrics =
        globalState.availableStringMetrics || [];

    const patchMetric = async (metricKey, displayOrder) => {
        try {
            const token = localStorage.getItem('token');

            if (!workingLocally && !token) {
                throw new Error('No token in local storage');
            }

            const payload = {
                feature_header: {
                    define_feature_data_simple_timeseries: {
                        [metricKey]: {
                            display_order: displayOrder
                        }
                    }
                }
            };

            console.log("PATCH PAYLOAD:", payload);

            if (workingLocally) {
                await axios.patch(
                    `http://localhost:3001/api/maps/${currentMapIdRef.current}`,
                    payload,
                    {
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        params: {
                            srid: 4326
                        }
                    }
                );
            } else {
                const url = devDeployment
                    ? `https://harrierapi9697.site/dev/api/maps/${currentMapIdRef.current}/?token=${encodeURIComponent(token)}&srid=4326`
                    : `https://harrierapi9697.site/api/maps/${currentMapIdRef.current}/?token=${encodeURIComponent(token)}&srid=4326`;

                const response = await fetch(url, {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    throw new Error(`PATCH failed: ${response.status}`);
                }

                await response.json();
            }
        } catch (err) {
            console.error(
                "PATCH failed:",
                err?.response?.data || err
            );
        }
    };

    const toggleMetric = (metricKey, currentlyHidden) => {
        const displayOrder = currentlyHidden ? 0 : -1;

        setMapMetaData(prev => {
            const next = structuredClone(prev);

            next.define_feature_data_simple_timeseries[metricKey] = {
                ...next.define_feature_data_simple_timeseries[metricKey],
                display_order: displayOrder
            };

            return next;
        });

        patchMetric(metricKey, displayOrder);
    };

    const { numericMetrics, stringMetrics } = useMemo(() => {
        const metricsMeta =
            mapMetaData?.define_feature_data_simple_timeseries || {};

        const numeric = availableNumericMetrics
            .map(key => ({
                key,
                meta: metricsMeta[key]
            }))
            .filter(item => item.meta);

        const string = availableStringMetrics
            .map(key => ({
                key,
                meta: metricsMeta[key]
            }))
            .filter(item => item.meta);

        const sortFn = (a, b) => {
            return (a.meta?.name || a.key).localeCompare(
                b.meta?.name || b.key,
                undefined,
                { sensitivity: 'base' }
            );
        };

        numeric.sort(sortFn);
        string.sort(sortFn);

        return {
            numericMetrics: numeric,
            stringMetrics: string
        };
    }, [
        mapMetaData,
        availableNumericMetrics,
        availableStringMetrics
    ]);

    const renderMetric = (key, meta) => {
        const isHidden = meta.display_order === -1;

        return (
            <div
                key={key}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px',
                    marginBottom: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: isHidden
                        ? '#f3f3f3'
                        : '#fff',
                }}
            >
                <span
                    style={{
                        flex: 1,
                        color: isHidden ? '#999' : '#222',
                    }}
                >
                    {meta?.name || key}
                </span>

                <button
                    onClick={() =>
                        toggleMetric(key, isHidden)
                    }
                    style={{
                        border: 'none',
                        background: 'none',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: isHidden ? '#999' : '#222',
                    }}
                    title={isHidden ? 'Show metric' : 'Hide metric'}
                >
                    {isHidden
                        ? <IoMdEyeOff />
                        : <IoMdEye />
                    }
                </button>
            </div>
        );
    };

    return (
        <div
            style={{
                width: '320px',
                maxHeight: '500px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10000000,
                borderRadius: '6px',
                padding: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <h4 style={{ margin: 0 }}>
                    Properties Filter
                </h4>

                <button
                    onClick={() =>
                        setShowFilterFeatureDataSimpleTimeseriesModal(false)
                    }
                    style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <IoClose />
                </button>
            </div>

            <h4 style={{ marginTop: 10 }}>
                Values
            </h4>

            {numericMetrics.map(({ key, meta }) =>
                renderMetric(key, meta)
            )}

            <h4 style={{ marginTop: 20 }}>
                Categories
            </h4>

            {stringMetrics.map(({ key, meta }) =>
                renderMetric(key, meta)
            )}
        </div>
    );
};

export default FilterFeatureDataSimpleTimeseriesModal;